import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Typography
} from '@mui/material';

interface Column {
  id: string;
  label: string;
  render: (lead: any) => React.ReactNode;
  minWidth?: number; 
}

interface LeadsTableProps {
  leads: any[];
  columns: Column[];
  totalProfiles: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedUsernames?: string[];
  onSelectLead?: (username: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  showCheckbox?: boolean;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  columns,
  totalProfiles,
  currentPage,
  setCurrentPage,
  selectedUsernames = [],
  onSelectLead,
  onSelectAll,
  showCheckbox = true,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery('(min-width:1300px)');
  const isMediumScreen = useMediaQuery('(min-width:900px)');
  const totalPages = Math.ceil(totalProfiles / 10);

  return (
    <TableContainer 
      component={Paper} 
      elevation={0} 
      sx={{ 
        border: '1px solid #EAECF0',
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Table size={isLargeScreen ? 'medium' : 'small'}>
        <TableHead>
          <TableRow>
            {showCheckbox && onSelectAll && (
              <TableCell 
                padding={isLargeScreen ? 'checkbox' : 'none'}
                sx={{ width: showCheckbox ? 48 : 0 }}
              >
                <Checkbox
                  indeterminate={selectedUsernames.length > 0 && selectedUsernames.length < leads.length}
                  checked={leads.length > 0 && selectedUsernames.length === leads.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  size={isLargeScreen ? 'medium' : 'small'}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell 
                key={column.id}
                sx={{ 
                  minWidth: column.minWidth,
                  padding: isLargeScreen ? '16px' : '8px',
                  fontSize: isLargeScreen ? '0.875rem' : '0.75rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {leads.length === 0 ? (
           <TableBody>
           <TableRow>
             <TableCell colSpan={columns.length + (showCheckbox ? 1 : 0)} sx={{ background: "#EAEEFB", padding: "20px" }}>
               <Typography sx={{ color: "#5175E0", textAlign: "center" }}>
                 No record found
               </Typography>
             </TableCell>
           </TableRow>
         </TableBody>
        ) : (

          <TableBody>
          {leads.map((lead, index) => (
            <TableRow 
              key={lead.username || index} 
              sx={{ background: index % 2 === 0 ? '#ffffff' : '#F9FAFB' }}
            >
              {showCheckbox && onSelectLead && (
                <TableCell 
                  padding={isLargeScreen ? 'checkbox' : 'none'}
                  sx={{ width: showCheckbox ? 48 : 0 }}
                >
                  <Checkbox
                    checked={selectedUsernames.includes(lead.username)}
                    onChange={(e) => onSelectLead(lead.username, e.target.checked)}
                    size={isLargeScreen ? 'medium' : 'small'}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell 
                key={column.id}
                sx={{ 
                  padding: isLargeScreen ? '16px' : '8px',
                  fontSize: isLargeScreen ? '12px' : '12px',
                }}
                >
                  {column.render(lead)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        )}
      </Table>
      {totalProfiles > 10 && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: isLargeScreen ? '8px 16px' : '4px 8px',
            flexDirection: isMediumScreen ? 'row' : 'column',
            gap: isMediumScreen ? 0 : 2
          }}
        >
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Button
              sx={{ 
                border: '1px solid #D0D5DD', 
                color: '#344054',
                fontSize: isLargeScreen ? '0.875rem' : '0.75rem',
                padding: isLargeScreen ? '6px 16px' : '4px 10px',
              }}
              size={isLargeScreen ? 'small' : 'small'}
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              sx={{ 
                border: '1px solid #D0D5DD', 
                color: '#344054',
                fontSize: isLargeScreen ? '0.875rem' : '0.75rem',
                padding: isLargeScreen ? '6px 16px' : '4px 10px',
              }}
              size={isLargeScreen ? 'small' : 'small'}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage * 10 >= totalProfiles}
            >
              Next
            </Button>
          </Box>
          <Box sx={{ fontSize: isLargeScreen ? '0.875rem' : '0.75rem' }}>
            Page {currentPage} of {totalPages}
          </Box>
        </Box>
      )}
    </TableContainer>
  );
};

export default LeadsTable;